import React, { useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';
import html2canvas from 'html2canvas';
import './ReceiptModal.css';

interface ReceiptModalProps {
  order: {
    _id: string;
    orderReference: string;
    createdAt: string;
    total: number;
    paymentMethod: string;
    paymentStatus: string;
    status: string;
    isPartPayment?: boolean;
    partPaymentDeposit?: number;
    remainingBalance?: number;
    totalPaid?: number;
    depositConfirmed?: boolean;
    items: any[];
    user?: { name: string; email: string };
    shippingAddress?: {
      fullName: string;
      address: string;
      city: string;
      state: string;
      phone: string;
    };
  };
  onClose: () => void;
}

const ReceiptModal: React.FC<ReceiptModalProps> = ({ order, onClose }) => {
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-NG', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatPrice = (price: number) => {
    return `₦${price.toLocaleString()}`;
  };

const captureAndShare = async () => {
  const receiptContent = document.getElementById('receipt-content');

  if (!receiptContent) {
    alert('Receipt content not found.');
    return;
  }

  const shareButton = document.querySelector(
    '.share-receipt-btn'
  ) as HTMLElement | null;

  let captureContainer: HTMLDivElement | null = null;

  try {
    if (shareButton) {
      shareButton.style.display = 'none';
    }

    // ------------------------------------------------------------
    // CREATE A TEMPORARY WIDE COPY FOR IMAGE CAPTURE
    // ------------------------------------------------------------
    captureContainer = document.createElement('div');

    captureContainer.style.position = 'fixed';
    captureContainer.style.left = '-10000px';
    captureContainer.style.top = '0';
    captureContainer.style.width = '760px';
    captureContainer.style.backgroundColor = '#ffffff';
    captureContainer.style.padding = '0';
    captureContainer.style.margin = '0';
    captureContainer.style.boxSizing = 'border-box';
    captureContainer.style.overflow = 'visible';
    captureContainer.style.zIndex = '-1';

    const receiptClone = receiptContent.cloneNode(
      true
    ) as HTMLElement;

    // ------------------------------------------------------------
    // FORCE THE CLONED RECEIPT TO USE THE FULL CAPTURE WIDTH
    // ------------------------------------------------------------
    receiptClone.style.width = '760px';
    receiptClone.style.maxWidth = 'none';
    receiptClone.style.minWidth = '760px';
    receiptClone.style.height = 'auto';
    receiptClone.style.minHeight = '0';
    receiptClone.style.maxHeight = 'none';
    receiptClone.style.overflow = 'visible';
    receiptClone.style.boxSizing = 'border-box';
    receiptClone.style.padding = '30px';
    receiptClone.style.margin = '0';
    receiptClone.style.backgroundColor = '#ffffff';

    // ------------------------------------------------------------
    // PAYMENT SUMMARY / OTHER FULL-WIDTH SECTIONS
    // ------------------------------------------------------------
    const paymentDetails = receiptClone.querySelector(
      '.receipt-payment-details'
    ) as HTMLElement | null;

    if (paymentDetails) {
      paymentDetails.style.width = '100%';
      paymentDetails.style.maxWidth = '100%';
      paymentDetails.style.boxSizing = 'border-box';
    }

    // ------------------------------------------------------------
    // ITEMS SECTION
    // ------------------------------------------------------------
    const itemsSection = receiptClone.querySelector(
      '.receipt-items'
    ) as HTMLElement | null;

    if (itemsSection) {
      itemsSection.style.width = '100%';
      itemsSection.style.maxWidth = '100%';
      itemsSection.style.boxSizing = 'border-box';
      itemsSection.style.marginLeft = '0';
      itemsSection.style.marginRight = '0';
    }

    // ------------------------------------------------------------
    // MAKE THE TABLE EXACTLY THE SAME WIDTH AS THE CONTENT AREA
    // ------------------------------------------------------------
    const table = receiptClone.querySelector(
      '.receipt-items-table'
    ) as HTMLTableElement | null;

    if (table) {
      table.style.display = 'table';
      table.style.width = '100%';
      table.style.minWidth = '100%';
      table.style.maxWidth = '100%';
      table.style.marginLeft = '0';
      table.style.marginRight = '0';
      table.style.padding = '0';
      table.style.boxSizing = 'border-box';
      table.style.tableLayout = 'fixed';
      table.style.borderCollapse = 'collapse';

      // ----------------------------------------------------------
      // EXPLICIT COLUMN WIDTHS
      //
      // Item    = 52%
      // Qty     = 10%
      // Price   = 19%
      // Subtotal= 19%
      //
      // This prevents the Subtotal column from being squeezed.
      // ----------------------------------------------------------
      const columnWidths = ['52%', '10%', '19%', '19%'];

      const headerCells = table.querySelectorAll('thead th');

      headerCells.forEach((cell, index) => {
        const element = cell as HTMLElement;

        if (columnWidths[index]) {
          element.style.width = columnWidths[index];
          element.style.minWidth = columnWidths[index];
          element.style.maxWidth = columnWidths[index];
        }

        element.style.boxSizing = 'border-box';
        element.style.whiteSpace = 'normal';
        element.style.overflow = 'visible';
        element.style.textOverflow = 'clip';
        element.style.wordBreak = 'normal';
      });

      // ----------------------------------------------------------
      // APPLY THE SAME WIDTHS TO BODY CELLS
      // ----------------------------------------------------------
      const bodyRows = table.querySelectorAll('tbody tr');

      bodyRows.forEach((row) => {
        const rowCells = row.querySelectorAll('td');

        rowCells.forEach((cell, index) => {
          const element = cell as HTMLElement;

          if (columnWidths[index]) {
            element.style.width = columnWidths[index];
            element.style.minWidth = columnWidths[index];
            element.style.maxWidth = columnWidths[index];
          }

          element.style.boxSizing = 'border-box';
          element.style.whiteSpace = 'normal';
          element.style.overflow = 'visible';
          element.style.textOverflow = 'clip';
          element.style.wordBreak = 'normal';
        });
      });
    }

    // ------------------------------------------------------------
    // MAKE TABLE CELLS FULLY VISIBLE
    // ------------------------------------------------------------
    const cells = receiptClone.querySelectorAll(
      '.receipt-items-table th, .receipt-items-table td'
    );

    cells.forEach((cell) => {
      const element = cell as HTMLElement;

      element.style.boxSizing = 'border-box';
      element.style.whiteSpace = 'normal';
      element.style.overflow = 'visible';
      element.style.textOverflow = 'clip';
      element.style.wordBreak = 'normal';
    });

    // ------------------------------------------------------------
    // MAKE SHIPPING SECTION FULL WIDTH
    // ------------------------------------------------------------
    const shippingSection = receiptClone.querySelector(
      '.receipt-shipping'
    ) as HTMLElement | null;

    if (shippingSection) {
      shippingSection.style.width = '100%';
      shippingSection.style.maxWidth = '100%';
      shippingSection.style.boxSizing = 'border-box';
    }

    // ------------------------------------------------------------
    // ADD CLONE TO DOCUMENT
    // ------------------------------------------------------------
    captureContainer.appendChild(receiptClone);
    document.body.appendChild(captureContainer);

    // Allow the browser to finish calculating the cloned layout.
    await new Promise<void>((resolve) => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          resolve();
        });
      });
    });

    // ------------------------------------------------------------
    // GET ACTUAL CLONE DIMENSIONS
    // ------------------------------------------------------------
    const captureWidth = Math.ceil(
      Math.max(
        receiptClone.scrollWidth,
        receiptClone.getBoundingClientRect().width,
        760
      )
    );

    const captureHeight = Math.ceil(
      Math.max(
        receiptClone.scrollHeight,
        receiptClone.getBoundingClientRect().height
      )
    );

    // ------------------------------------------------------------
    // CAPTURE THE CLONED RECEIPT
    // ------------------------------------------------------------
    const canvas = await html2canvas(receiptClone, {
      scale: 2,
      backgroundColor: '#ffffff',
      useCORS: true,
      logging: false,

      width: captureWidth,
      height: captureHeight,

      windowWidth: captureWidth,
      windowHeight: captureHeight,

      x: 0,
      y: 0,

      scrollX: 0,
      scrollY: 0,
    });

    // ------------------------------------------------------------
    // REMOVE TEMPORARY CLONE
    // ------------------------------------------------------------
    captureContainer.remove();
    captureContainer = null;

    // ------------------------------------------------------------
    // CONVERT CANVAS TO PNG
    // ------------------------------------------------------------
    const imageData = canvas.toDataURL('image/png');

    const response = await fetch(imageData);
    const blob = await response.blob();

    const fileName = `Gimbiya-Value-Store-Receipt-${order.orderReference}.png`;

    // ------------------------------------------------------------
    // NATIVE CAPACITOR SHARE
    // ------------------------------------------------------------
    if (Capacitor.isNativePlatform()) {
      const base64Data = imageData.split(',')[1];

      await Filesystem.writeFile({
        path: fileName,
        data: base64Data,
        directory: Directory.Cache,
      });

      const fileUri = await Filesystem.getUri({
        path: fileName,
        directory: Directory.Cache,
      });

      await Share.share({
        title: 'Gimbiya Value Store Receipt',
        text: `Receipt ${order.orderReference}`,
        url: fileUri.uri,
        dialogTitle: 'Share Receipt',
      });

            // Clean up the temporary receipt file after sharing.
      try {
        await Filesystem.deleteFile({
          path: fileName,
          directory: Directory.Cache,
        });
      } catch (cleanupError) {
        console.warn(
          'Receipt temporary-file cleanup failed:',
          cleanupError
        );
      }
    } else {
      // ----------------------------------------------------------
      // BROWSER SHARE
      // ----------------------------------------------------------
      const file = new File(
        [blob],
        fileName,
        {
          type: 'image/png',
        }
      );

      if (
        navigator.share &&
        navigator.canShare &&
        navigator.canShare({ files: [file] })
      ) {
        await navigator.share({
          title: 'Gimbiya Value Store Receipt',
          text: `Receipt ${order.orderReference}`,
          files: [file],
        });
            } else {
        // --------------------------------------------------------
        // FALLBACK: DOWNLOAD IMAGE
        // --------------------------------------------------------
        const url = URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;

        document.body.appendChild(link);
        link.click();
        link.remove();

        setTimeout(() => {
          URL.revokeObjectURL(url);
        }, 1000);

        alert(
          'Receipt sharing is not supported by this browser. ' +
          'The receipt image has been downloaded instead.'
        );
      }
    }
  } catch (error) {
    console.error('❌ Error sharing receipt:', error);

    alert(
      'Unable to create the receipt image. Please try again.'
    );
  } finally {
    // Remove the temporary capture element if an error
    // occurred before the normal cleanup.
    if (captureContainer) {
      captureContainer.remove();
    }

    // Always restore the Share button.
    if (shareButton) {
      shareButton.style.display = '';
    }
  }
};



  const printReceipt = () => {
    const receiptContent = document.getElementById('receipt-content');
    if (!receiptContent) return;

    const printWindow = window.open('', '_blank');
    if (!printWindow) {
        alert('Please allow pop-ups to print the receipt');
        return;
    }

    printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
        <title>Receipt - ${order.orderReference || order._id.slice(-8)}</title>
        <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { 
            font-family: 'Helvetica Neue', Arial, sans-serif; 
            padding: 40px; 
            background: white; 
            }
            .receipt-container { 
            max-width: 800px; 
            margin: 0 auto; 
            }
            .receipt-store-info { 
            text-align: center; 
            margin-bottom: 30px; 
            padding-bottom: 20px; 
            border-bottom: 2px solid #333; 
            }
            .receipt-store-info h2 { 
            margin-bottom: 10px; 
            }
            .receipt-logo {
            width: 60px;
            height: 60px;
            margin-bottom: 10px;
            border-radius: 8px !important;
            }
            
            .receipt-header { 
            margin-bottom: 25px; 
            padding: 15px; 
            background: #f9f9f9; 
            border: 1px solid #ddd;
            }

            .naira-bag-icon {
              width: 32px !important;
              height: 32px !important;
              object-fit: contain;
              flex: 0 0 32px;
              display: block;
            }

            .summary-header {
                display: flex;
                align-items: center;
                gap: 8px;
            }
            .receipt-payment-details { 
            margin-bottom: 25px; 
            padding: 15px; 
            border: 1px solid #ddd; 
            background: #fff8e1; 
            }
            .receipt-items-table { 
            width: 100%; 
            border-collapse: collapse; 
            margin-bottom: 25px; 
            }
            .receipt-items-table th, 
            .receipt-items-table td { 
            padding: 10px; 
            text-align: left; 
            border: 1px solid #ddd; 
            }
            .receipt-items-table th { 
            background: #f0f0f0; 
            }
            .receipt-shipping {
            background: #e3f2fd;
            padding: 15px;
            border-radius: 8px;
            margin-bottom: 20px;
            }
            .receipt-footer { 
            margin-top: 30px; 
            padding-top: 20px; 
            text-align: center; 
            border-top: 1px solid #ddd; 
            font-size: 12px; 
            color: #666; 
            }
            .payment-row {
            display: flex;
            justify-content: space-between;
            padding: 8px 0;
            border-bottom: 1px solid #ffe0b2;
            }
            .payment-row:last-child {
            border-bottom: none;
            }
            @media print { 
            body { padding: 20px; } 
            .no-print { display: none; }
            .receipt-logo {
                width: 60px;
                height: 60px;
            }
            }
        </style>
        </head>
        <body>
        <div class="receipt-container">
            ${receiptContent.innerHTML}
        </div>
        </body>
        </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const isPartPayment = order.isPartPayment === true;
  const depositAmount = order.partPaymentDeposit || order.totalPaid || 0;
  const remainingBalance = order.remainingBalance || 0;
  const isFullyPaid = order.paymentStatus === 'paid';
  const isDepositConfirmed = order.depositConfirmed === true;

  return (
    <div className="receipt-modal-overlay" onClick={onClose}>
      <div className="receipt-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="receipt-modal-header">
          <h2>🧾 Payment Receipt</h2>
          <button className="receipt-close-btn" onClick={onClose}>×</button>
        </div>

        <div id="receipt-content" className="receipt-content">
          {/* Store Info */}
          <div className="receipt-store-info">
            <img src="/Gimbiya.png" alt="Gimbiya" className="receipt-logo" />
            <h2>Gimbiya Value Store</h2>
            <p>Zango, Zaria, Kaduna State, Nigeria</p>
            <p>📞 +234 701 011 0977 | ✉️ hello@gimbiyastore.com</p>
            <p>🌍 www.gimbiyastore.com</p>
          </div>

          {/* Receipt Header */}
          <div className="receipt-header">
            <h3>PAYMENT RECEIPT</h3>
            <p><strong>Receipt No:</strong> {order.orderReference || order._id.slice(-8)}</p>
            <p><strong>Date:</strong> {formatDate(order.createdAt)}</p>
            <p><strong>Order Status:</strong> {order.status.toUpperCase()}</p>
            <p><strong>Payment Method:</strong> Paystack</p>
          </div>

          {/* Payment Summary */}
          <div className="receipt-payment-details">
            <div className="summary-header">
                <img src="/money_bag_1_trans.png" alt="Naira Bag" className="naira-bag-icon"/>
                <h4>Payment Summary</h4>
            </div>
            <div className="payment-row">
              <span>Total Order Amount:</span>
              <strong>{formatPrice(order.total)}</strong>
            </div>
            
            {isPartPayment ? (
              <>
                <div className="payment-row deposit">
                  <span>Deposit Paid:</span>
                  <strong style={{ color: '#4caf50' }}>{formatPrice(depositAmount)}</strong>
                </div>
                <div className="payment-row balance">
                  <span>Remaining Balance:</span>
                  <strong style={{ color: remainingBalance > 0 ? '#ff9800' : '#4caf50' }}>
                    {formatPrice(remainingBalance)}
                  </strong>
                </div>
                <div className="payment-row status">
                  <span>Deposit Status:</span>
                  <strong style={{ color: isDepositConfirmed ? '#4caf50' : '#ff9800' }}>
                    {isDepositConfirmed ? '✓ Confirmed' : 'Pending Confirmation'}
                  </strong>
                </div>
                {isFullyPaid && (
                  <div className="payment-row fully-paid">
                    <span>Final Status:</span>
                    <strong style={{ color: '#4caf50' }}>✓ FULLY PAID</strong>
                  </div>
                )}
              </>
            ) : (
              <div className="payment-row">
                <span>Amount Paid:</span>
                <strong style={{ color: '#4caf50' }}>{formatPrice(order.total)}</strong>
              </div>
            )}
          </div>

          {/* Items Summary */}
          <div className="receipt-items">
            <h4>📦 Items</h4>
            <table className="receipt-items-table">
              <thead>
                <tr>
                  <th>Item</th>
                  <th>Qty</th>
                  <th>Price</th>
                  <th>Subtotal</th>
                </tr>
              </thead>
              <tbody>
                {order.items.map((item, idx) => (
                  <tr key={idx}>
                    <td>{item.name}</td>
                    <td>{item.quantity}</td>
                    <td>{formatPrice(item.price)}</td>
                    <td>{formatPrice(item.price * item.quantity)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Shipping Address */}
          {order.shippingAddress && (
            <div className="receipt-shipping">
              <h4>🚚 Shipping Address</h4>
              <p><strong>{order.shippingAddress.fullName}</strong></p>
              <p>{order.shippingAddress.address}</p>
              <p>{order.shippingAddress.city}, {order.shippingAddress.state}</p>
              <p>📞 {order.shippingAddress.phone}</p>
            </div>
          )}

          {/* Footer */}
          <div className="receipt-footer">
            <p>Thank you for shopping with Gimbiya Value Store!</p>
            <p>For inquiries, please contact us at hello@gimbiyastore.com</p>
          </div>
        </div>

        <div className="receipt-modal-buttons">
          <button className="print-receipt-btn" onClick={printReceipt}>
            🖨️ Print Receipt
          </button>
          <button className="share-receipt-btn" onClick={captureAndShare}>
            📸 Share Receipt
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReceiptModal;